import axios from 'axios';

const WEBHOOK_URL = 'https://webhook-editor.abianca.com.br/webhook/41ae71d3-dc9a-47d1-8d22-c0334ba645e6';

/**
 * Formata o número de telefone para o padrão internacional brasileiro: 5511987654321
 * @param {string} phoneNumber - Número de telefone do usuário
 * @returns {string} - Número formatado
 */
const formatPhoneNumber = (phoneNumber) => {
  // Remove todos os caracteres não numéricos
  const numbers = phoneNumber.replace(/\D/g, '');
  
  // Se o número já começa com 55, retorna como está
  if (numbers.startsWith('55')) {
    return numbers;
  }
  
  // Adiciona o prefixo 55 (Brasil) no início do número
  return '55' + numbers;
};

/**
 * Serviço para lidar com autenticação e verificação de código
 */
const authService = {
  // Exportar URL do webhook para uso em outros componentes
  WEBHOOK_URL,
  
  /**
   * Envia o número de telefone para o webhook para solicitar um código de verificação
   * @param {string} phoneNumber - Número de telefone do usuário
   * @returns {Promise<string>} - 'sucesso' ou 'fracasso'
   */
  requestVerificationCode: async (phoneNumber) => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      // Primeiro webhook: apenas com o telefone formatado
      const response = await axios.post(WEBHOOK_URL, { 
        telefone: formattedPhone 
      });
      
      return response.data;
    } catch (error) {
      console.error('Erro ao solicitar código de verificação:', error);
      throw error;
    }
  },
  
  /**
   * Verifica o código de verificação enviado pelo usuário
   * @param {string} phoneNumber - Número de telefone do usuário
   * @param {string} code - Código de verificação
   * @returns {Promise<string>} - 'sucesso' ou 'fracasso'
   */
  verifyCode: async (phoneNumber, code) => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      const response = await axios.post(WEBHOOK_URL, { 
        telefone: formattedPhone,
        codigo: code
      });
      
      // Se a verificação foi bem-sucedida, salva apenas o telefone no localStorage
      if (response.data === 'sucesso') {
        // Criar objeto com dados do usuário (apenas telefone)
        const userData = {
          telefone: formattedPhone
        };
        
        // Salvar os dados no localStorage
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('userPhone', formattedPhone);
        
        return 'sucesso';
      }
      
      // Retorna a resposta da API
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      throw error;
    }
  },
  
  /**
   * Solicita o reenvio do código de verificação
   * @param {string} phoneNumber - Número de telefone do usuário
   * @returns {Promise<string>} - 'sucesso' ou 'fracasso'
   */
  resendVerificationCode: async (phoneNumber) => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      const response = await axios.post(WEBHOOK_URL, { 
        telefone: formattedPhone,
        acao: 'reenviar'
      });
      
      return response.data;
    } catch (error) {
      console.error('Erro ao reenviar código:', error);
      throw error;
    }
  },
  
  /**
   * Envia os dados complementares do usuário para o webhook
   * @param {Object} userData - Dados do usuário a serem enviados
   * @returns {Promise<string>} - 'sucesso' ou 'fracasso'
   */
  registerUserData: async (userData) => {
    try {
      // Enviar apenas os dados necessários para o webhook
      const response = await axios.post(WEBHOOK_URL, {
        ...userData,
        acao: 'registrar'
      });
      
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar dados do usuário:', error);
      throw error;
    }
  },
  
  /**
   * Verifica se o usuário está autenticado
   * @returns {boolean} - true se o usuário estiver autenticado, false caso contrário
   */
  isAuthenticated: () => {
    return localStorage.getItem('userData') !== null;
  },
  
  /**
   * Obtém os dados do usuário armazenados no localStorage
   * @returns {Object|null} - Dados do usuário ou null se não estiver autenticado
   */
  getUserData: () => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Erro ao analisar dados do usuário:', error);
      return null;
    }
  },
  
  /**
   * Armazena os dados do usuário no localStorage
   * @param {Object} userData - Dados do usuário
   */
  setUserData: (userData) => {
    localStorage.setItem('userData', JSON.stringify(userData));
  },
  
  /**
   * Remove os dados do usuário do localStorage (logout)
   */
  logout: () => {
    localStorage.removeItem('userData');
    localStorage.removeItem('userPhone');
  }
};

export default authService;
